import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';

type ChatSender = 'user' | 'bot';

interface ChatCard {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}

interface ChatMessage {
  sender: ChatSender;
  text: string;
  type?: 'text' | 'cards' | 'list';
  cards?: ChatCard[];
  listItems?: string[];
  linkText?: string;
  route?: string[];
  queryParams?: Record<string, string>;
  suggestedActions?: string[];
  timestamp?: Date;
}

interface ChatQuickAction {
  label: string;
  svg: SafeHtml;
  message: string;
}

const SVG = {
  pulse:    `<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  pending:  `<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/><polyline points="12 7 12 12 15.5 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  onboard:  `<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2"/><line x1="19" y1="8" x2="19" y2="14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="22" y1="11" x2="16" y2="11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  payroll:  `<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="12" r="2" stroke="currentColor" stroke-width="2"/><path d="M6 12h.01M18 12h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  leave:    `<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M23 12a11.05 11.05 0 0 0-22 0zm-5 7a3 3 0 0 1-6 0v-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  finger:   `<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4M14 13.12c0 2.38 0 6.38-1 8.88M17.29 21.02c.12-.6.43-2.3.5-3.02M3 9a9 9 0 0 1 15-6.7M3 14.5a9 9 0 0 0 .7 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M7 4.51A9 9 0 0 1 21 12c0 .77-.08 1.5-.21 2.23M21 15.91A9 9 0 0 1 9 21.1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  receipt:  `<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><polyline points="14 2 14 8 20 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
  sun:      `<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="2"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`,
};

interface IntentResponse {
  response: string;
  suggestedActions: string[];
  linkText?: string;
  route?: string[];
  queryParams?: Record<string, string>;
}

const INTENT_RESPONSES: Record<string, IntentResponse> = {
  greeting: {
    response: "Hello! I'm your HRMS assistant. How can I help you today?",
    suggestedActions: ['Check my leave balance', 'View attendance', 'Apply for leave', 'View holidays']
  },
  help: {
    response: 'I can help you with:\n• Leave management\n• Attendance & check-in/out\n• Holiday calendar\n• Payslips & payroll\n• Announcements\n• HR policies\n\nWhat would you like to know?',
    suggestedActions: ['Leave balance', 'Attendance today', 'Upcoming holidays', 'Latest payslip']
  },
  leave_balance: {
    response: 'Fetching your leave balance…',
    suggestedActions: ['Apply for leave', 'Leave status', 'Leave policy']
  },
  leave_apply: {
    response: 'To apply for leave:\n1. Open Leave Management\n2. Click "Apply for Leave"\n3. Select type, dates & duration\n4. Add your reason\n5. Submit — your manager will be notified',
    suggestedActions: ['Check leave balance', 'Leave policy'],
    linkText: 'Open Leave Management',
    route: ['/leaves']
  },
  leave_status: {
    response: 'Fetching your recent leave applications…',
    suggestedActions: ['Apply for leave', 'Leave balance', 'Leave policy'],
    linkText: 'Open Leave History',
    route: ['/leaves'],
    queryParams: { tab: 'history' }
  },
  leave_policy: {
    response: 'Leave policies:\n• Casual Leave — Personal reasons\n• Sick Leave — Health issues\n• Earned Leave — Accrued from work duration\n\nContact HR for accrual & carry-forward details.',
    suggestedActions: ['Leave balance', 'Apply for leave', 'Contact HR'],
    linkText: 'Open Leave Management',
    route: ['/leaves']
  },
  attendance_checkin: {
    response: 'To check in, open the Attendance section and click "Check In". If geo-location attendance is enabled, make sure you\'re within the allowed zone.',
    suggestedActions: ['Check out', 'Attendance history', 'Attendance policy'],
    linkText: 'Open Attendance',
    route: ['/attendance']
  },
  attendance_checkout: {
    response: 'To check out, go to Attendance and click "Check Out". Your total working hours are calculated automatically.',
    suggestedActions: ['Check in', 'Attendance history', 'Working hours'],
    linkText: 'Open Attendance',
    route: ['/attendance']
  },
  attendance_history: {
    response: 'Your attendance history shows daily check-in/out times, total hours, and status (Present, Absent, Late).',
    suggestedActions: ["Today's attendance", 'Monthly report', 'Regularise attendance'],
    linkText: 'Open Attendance',
    route: ['/attendance']
  },
  attendance_monthly: {
    response: 'Fetching your attendance for this month…',
    suggestedActions: ["Today's attendance", 'Attendance history', 'Leave balance'],
    linkText: 'Open Attendance',
    route: ['/attendance']
  },
  attendance_today: {
    response: 'Fetching your attendance for today…',
    suggestedActions: ['Monthly attendance', 'Attendance history', 'Check in'],
    linkText: 'Open Attendance',
    route: ['/attendance']
  },
  attendance_regularize: {
    response: 'To regularise attendance:\n1. Open Attendance\n2. Go to Regularise tab\n3. Select date & correction type\n4. Enter check-in/out & reason\n5. Submit for manager approval',
    suggestedActions: ['Attendance history', 'Contact manager', 'Attendance policy'],
    linkText: 'Open Regularisation',
    route: ['/attendance'],
    queryParams: { tab: 'regularise' }
  },
  holiday_list: {
    response: 'Fetching the holiday calendar…',
    suggestedActions: ['Apply for leave', 'Leave balance', 'Upcoming holidays'],
    linkText: 'Open Holidays',
    route: ['/holidays']
  },
  holiday_upcoming: {
    response: 'Fetching upcoming holidays…',
    suggestedActions: ['Full holiday list', 'Apply for leave', 'Leave balance'],
    linkText: 'Open Holidays',
    route: ['/holidays']
  },
  payroll_salary: {
    response: 'Your salary details and payslips are in the Payroll section. Payslips are generated at the end of each month.',
    suggestedActions: ['Latest payslip', 'Tax details', 'Salary structure'],
    linkText: 'Open Payroll',
    route: ['/my-payslips']
  },
  payroll_payslip: {
    response: 'Fetching your latest payslips…',
    suggestedActions: ['Salary details', 'Tax information', 'Payment history'],
    linkText: 'Open My Payslips',
    route: ['/my-payslips']
  },
  payroll_date: {
    response: 'Salary is typically credited on the last working day of each month. If it falls on a weekend/holiday, it is credited on the previous working day. Contact HR for exact dates.',
    suggestedActions: ['Latest payslip', 'Salary details', 'Contact HR']
  },
  employee_profile: {
    response: 'You can view and update your profile — personal details, contact info, emergency contacts, and more.',
    suggestedActions: ['Update profile', 'Change password', 'View documents'],
    linkText: 'Open Profile',
    route: ['/profile']
  },
  employee_documents: {
    response: 'Your documents (ID proofs, certificates) are managed from the Profile area. Contact HR if you need to update official records.',
    suggestedActions: ['Upload document', 'Profile', 'Contact HR'],
    linkText: 'Open Profile',
    route: ['/profile']
  },
  company_announcements: {
    response: 'Fetching latest announcements…',
    suggestedActions: ['Dashboard', 'Events', 'Contact HR'],
    linkText: 'Open Announcements',
    route: ['/announcements']
  },
  company_policy: {
    response: 'Company policies are available in Announcements. This includes HR policies, attendance rules, code of conduct, and organisational guidelines.',
    suggestedActions: ['Leave policy', 'Attendance policy', 'Contact HR'],
    linkText: 'Open Announcements',
    route: ['/announcements']
  },
  performance_review: {
    response: 'Performance reviews are conducted periodically. You can view review cycles, goals, and feedback in the Performance section.',
    suggestedActions: ['Set goals', 'View feedback', 'Performance cycle'],
    linkText: 'Open Performance',
    route: ['/performance']
  },
  performance_goals: {
    response: 'You can set and track SMART goals in the Performance section. Discuss them with your manager for alignment.',
    suggestedActions: ['Performance review', 'View goals', 'Manager feedback'],
    linkText: 'Open Performance',
    route: ['/performance']
  },
  default: {
    response: "I'm not sure I understood that. I can help with leave, attendance, holidays, payroll, and HR info. Try rephrasing or pick from:",
    suggestedActions: ['Leave balance', 'Attendance', 'Holidays', 'Payslip', 'Help']
  },
  human_handoff: {
    response: "I'll connect you with our HR team for personalised help. You can also reach HR at hr@company.com or via your internal HR helpline.",
    suggestedActions: ['Back to chat', 'Email HR', 'Leave balance']
  }
};

const HR_INTENT_RESPONSES: Record<string, IntentResponse> = {
  greeting: {
    response: "Hello! I'm your HRMS assistant. What would you like to check today?",
    suggestedActions: ["Today's pulse", 'Pending leave approvals', 'Onboarding pipeline', 'Payroll status']
  },
  help: {
    response: 'As HR, I can help you with:\n• Daily attendance pulse\n• Pending leave approvals\n• Headcount & org overview\n• Onboarding pipeline\n• Payroll status\n• Announcements & policies\n\nWhat do you need?',
    suggestedActions: ["Today's pulse", 'Pending approvals', 'Headcount', 'Onboarding']
  },
  hr_dashboard: {
    response: "Fetching today's org pulse…",
    suggestedActions: ['Pending leave approvals', 'Who is absent today', 'Onboarding pipeline']
  },
  hr_pending_leaves: {
    response: 'Fetching pending leave requests…',
    suggestedActions: ["Today's pulse", 'Attendance today', 'Headcount'],
    linkText: 'Open Leave Management',
    route: ['/leaves']
  },
  hr_attendance_today: {
    response: "Fetching today's attendance overview…",
    suggestedActions: ['Pending leave approvals', "Today's pulse", 'Headcount'],
    linkText: 'Open Attendance',
    route: ['/attendance']
  },
  hr_headcount: {
    response: 'Fetching employee headcount…',
    suggestedActions: ['Onboarding pipeline', 'Attendance today', "Today's pulse"],
    linkText: 'Open Employees',
    route: ['/employees']
  },
  hr_onboarding: {
    response: 'Fetching onboarding pipeline…',
    suggestedActions: ['Headcount', "Today's pulse", 'Pending leave approvals'],
    linkText: 'Open Onboarding',
    route: ['/onboarding']
  },
  hr_payroll_status: {
    response: 'Fetching payroll status for this month…',
    suggestedActions: ["Today's pulse", 'Headcount', 'Pending leave approvals'],
    linkText: 'Open Payroll',
    route: ['/payroll']
  },
  default: {
    response: "I'm not sure I understood that. As HR assistant, I can help with attendance, leaves, headcount, onboarding, and payroll. Try:",
    suggestedActions: ["Today's pulse", 'Pending approvals', 'Headcount', 'Onboarding', 'Help']
  },
};

const INTENT_KEYWORDS: Record<string, string[]> = {
  greeting: ['hello', 'hi', 'hey', 'good morning', 'good afternoon', 'good evening', 'greetings', 'howdy'],
  help: ['help', 'assist', 'support', 'what can you do', 'capabilities', 'guide', 'how to use'],
  leave_balance: [
    'leave balance', 'balance leave', 'how many leave', 'remaining leave', 'leave available',
    'leave quota', 'my leave', 'my leave balance', 'show leave', 'show my leave', 'present leave',
    'remaining leaves', 'available leaves', 'leave remaining', 'current leave balance', 'check leave balance'
  ],
  leave_apply: ['how to apply leave', 'apply leave', 'apply for leave', 'request leave', 'take leave', 'want leave', 'need leave', 'leave application', 'submit leave'],
  leave_status: ['leave status', 'leave application status', 'check leave status', 'track leave', 'leave approved', 'leave rejected', 'pending leave', 'my leave applications', 'leave history'],
  leave_policy: ['leave policy', 'leave rule', 'leave regulation', 'how leave works', 'leave entitlement', 'leave types', 'casual leave', 'sick leave', 'earned leave'],
  attendance_checkin: ['check in', 'checkin', 'punch in', 'mark attendance', 'clock in', 'sign in', 'how to check in'],
  attendance_checkout: ['check out', 'checkout', 'punch out', 'clock out', 'sign out', 'how to check out'],
  attendance_history: ['attendance history', 'attendance record', 'my attendance', 'past attendance', 'attendance log', 'attendance report'],
  attendance_monthly: ['monthly attendance', 'this month attendance', 'month attendance', 'attendance this month', 'monthly report', "this month's attendance", 'my monthly attendance', 'attendance summary', 'view attendance'],
  attendance_today: ['today attendance', 'attendance today', "today's attendance", 'my attendance today', 'check today attendance', 'today status', 'today present', 'am i present today', 'today checkin'],
  attendance_regularize: ['regularize', 'regularise', 'regularisation', 'regularization', 'attendance correction', 'fix attendance', 'update attendance'],
  holiday_upcoming: ['upcoming holiday', 'next holiday', 'coming holiday', 'holiday schedule', 'when is holiday', 'holidays this month'],
  holiday_list: ['holiday', 'holidays', 'holiday list', 'holiday calendar', 'public holiday', 'company holiday', 'view holidays'],
  payroll_payslip: ['payslip', 'salary slip', 'pay slip', 'download payslip', 'payslip download', 'salary statement', 'latest payslip', 'my payslip'],
  payroll_date: ['salary date', 'payday', 'payment date', 'when salary', 'salary credit', 'pay date', 'when will i get salary'],
  payroll_salary: ['salary', 'payment', 'earnings', 'income', 'salary details', 'salary information', 'payroll', 'ctc'],
  employee_documents: ['document', 'documents', 'upload document', 'my documents', 'id proof', 'certificates'],
  employee_profile: ['profile', 'my profile', 'personal details', 'update profile', 'edit profile', 'my information'],
  company_announcements: ['announcement', 'announcements', 'news', 'updates', 'notification', 'company news', 'latest news'],
  company_policy: ['policy', 'policies', 'company policy', 'hr policy', 'rules', 'regulations', 'guidelines'],
  performance_review: ['performance review', 'appraisal', 'evaluation', 'performance assessment', 'review cycle'],
  performance_goals: ['goals', 'performance goals', 'objectives', 'targets', 'kpi', 'set goals', 'my goals'],
  human_handoff: ['human', 'talk to human', 'speak to person', 'real person', 'agent', 'support team', 'hr team', 'contact hr', 'connect hr'],

  hr_dashboard:        ['daily briefing', 'summary', "today's pulse", 'pulse', 'overview', 'org summary', 'company summary', 'daily summary', 'stats', 'dashboard', 'snapshot'],
  hr_pending_leaves:   ['pending leave', 'pending leaves', 'leave approvals', 'approve leave', 'leave requests', 'leaves to approve', 'unapproved leave', 'leave pending'],
  hr_attendance_today: ['who is absent', "who's absent", 'absent today', 'team attendance', 'present today', 'attendance overview', 'how many present', 'today headcount', 'check ins today'],
  hr_headcount:        ['headcount', 'total employees', 'employee count', 'how many employees', 'staff count', 'new joiners', 'new employees', 'joiners this month', 'active employees'],
  hr_onboarding:       ['onboarding', 'candidates', 'new joiners pipeline', 'onboarding status', 'pending onboarding', 'candidate status', 'invite candidates'],
  hr_payroll_status:   ['payroll status', 'payroll this month', 'run payroll', 'salary processed', 'payroll generated', 'payroll summary', 'process payroll'],
};

@Component({
  selector: 'app-chatbot',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './chatbot.component.html',
  styleUrl: './chatbot.component.scss',
})
export class Chatbot implements OnInit {
  @ViewChild('messagesContainer') private messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('chatInput') chatInput!: ElementRef;

  isOpen = false;
  isSending = false;
  currentMessage = '';
  unreadCount = 0;
  topicsOpen = false;

  messages: ChatMessage[] = [];

  get isHrRole(): boolean {
    const role = (this.authService.getUserRole() || '').toLowerCase().replace(/^role_/, '');
    return ['admin', 'hr', 'hr_admin', 'super_admin'].includes(role);
  }

  get quickActions(): ChatQuickAction[] {
    if (this.isHrRole) {
      return [
        { label: "Today's Pulse",  svg: this.safe(SVG.pulse),   message: "Today's pulse" },
        { label: 'Pending Leaves', svg: this.safe(SVG.pending),  message: 'Pending leave approvals' },
        { label: 'Onboarding',     svg: this.safe(SVG.onboard),  message: 'Onboarding pipeline' },
        { label: 'Payroll',        svg: this.safe(SVG.payroll),  message: 'Payroll status this month' },
      ];
    }
    return [
      { label: 'Leave Balance', svg: this.safe(SVG.leave),   message: 'Check my leave balance' },
      { label: 'Attendance',    svg: this.safe(SVG.finger),  message: "Today's attendance" },
      { label: 'Payslip',       svg: this.safe(SVG.receipt), message: 'My latest payslip' },
      { label: 'Holidays',      svg: this.safe(SVG.sun),     message: 'Upcoming holidays' },
    ];
  }

  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private sanitizer: DomSanitizer
  ) {}

  safe(svg: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(svg);
  }

  ngOnInit(): void {
    const isHr = this.isHrRole;
    this.messages = [{
      sender: 'bot',
      text: isHr
        ? "Hello! I'm your HR assistant. Ask me anything — attendance, leaves, onboarding, payroll, and more."
        : "Hello! I'm your HRMS assistant. How can I help you today?",
      suggestedActions: isHr
        ? ["Today's pulse", 'Pending leave approvals', 'Onboarding pipeline', 'Payroll status']
        : ['Check my leave balance', 'View attendance', 'Apply for leave', 'View holidays'],
      timestamp: new Date()
    }];
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) this.closeChat();
  }

  toggleChat(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.unreadCount = 0;
      setTimeout(() => this.chatInput?.nativeElement.focus(), 80);
      this.scrollToLatest();
    }
  }

  closeChat(): void {
    this.isOpen = false;
  }

  get currentTopics(): { label: string; desc: string; svg: SafeHtml; message: string }[] {
    if (this.isHrRole) {
      return [
        { label: "Today's Pulse",       desc: 'Attendance, leaves & org overview',    svg: this.safe(SVG.pulse),   message: "Today's pulse" },
        { label: 'Pending Leave Approvals', desc: 'Leaves waiting for your action',   svg: this.safe(SVG.pending), message: 'Pending leave approvals' },
        { label: 'Who Is Absent Today', desc: "Today's attendance overview",           svg: this.safe(SVG.finger),  message: 'Who is absent today' },
        { label: 'Onboarding Pipeline', desc: 'Candidates status breakdown',           svg: this.safe(SVG.onboard), message: 'Onboarding pipeline' },
        { label: 'Payroll Status',      desc: "This month's payroll progress",         svg: this.safe(SVG.payroll), message: 'Payroll status this month' },
        { label: 'Headcount',           desc: 'Active employees & new joiners',        svg: this.safe(SVG.onboard), message: 'Headcount' },
        { label: 'Announcements',       desc: 'Latest company announcements',          svg: this.safe(SVG.sun),     message: 'Latest announcements' },
      ];
    }
    return [
      { label: 'Leave Balance',       desc: 'Check your remaining leave days',        svg: this.safe(SVG.leave),   message: 'Check my leave balance' },
      { label: 'Apply for Leave',     desc: 'Steps to submit a leave request',        svg: this.safe(SVG.pending), message: 'How to apply for leave' },
      { label: 'Leave Status',        desc: 'Track your leave applications',          svg: this.safe(SVG.receipt), message: 'My leave application status' },
      { label: "Today's Attendance",  desc: 'Check-in, check-out & hours today',      svg: this.safe(SVG.finger),  message: "Today's attendance" },
      { label: 'Monthly Attendance',  desc: 'Your attendance summary this month',     svg: this.safe(SVG.pulse),   message: 'Monthly attendance' },
      { label: 'Regularise Attendance', desc: 'Fix missing or wrong attendance',      svg: this.safe(SVG.finger),  message: 'How to regularise attendance' },
      { label: 'My Payslip',          desc: 'View your latest salary slip',           svg: this.safe(SVG.receipt), message: 'My latest payslip' },
      { label: 'Upcoming Holidays',   desc: 'See holidays coming up',                 svg: this.safe(SVG.sun),     message: 'Upcoming holidays' },
      { label: 'Announcements',       desc: 'Latest company news & updates',          svg: this.safe(SVG.sun),     message: 'Latest announcements' },
    ];
  }

  sendTopicMessage(message: string): void {
    this.topicsOpen = false;
    this.sendChatMessage(message);
  }

  runQuickAction(action: ChatQuickAction): void {
    this.sendChatMessage(action.message);
  }

  runSuggestedAction(action: string): void {
    if (action.toLowerCase() === 'email hr') {
      window.location.href = 'mailto:hr@company.com';
      return;
    }
    this.sendChatMessage(action);
  }

  sendMessage(): void {
    this.sendChatMessage(this.currentMessage);
  }

  private sendChatMessage(text: string): void {
    const message = text.trim();
    if (!message || this.isSending) return;

    if (!this.getCurrentEmployeeId()) {
      this.pushBotMessage({ text: 'Please log in again before using the assistant.' });
      return;
    }

    this.isOpen = true;
    this.unreadCount = 0;
    this.messages = [...this.messages, { sender: 'user', text: message, timestamp: new Date() }];
    this.currentMessage = '';
    this.isSending = true;
    this.scrollToLatest();

    setTimeout(() => {
      this.getFrontendReply(message).then(reply => {
        this.messages = [...this.messages, { ...reply, timestamp: new Date() }];
        this.isSending = false;
        if (!this.isOpen) this.unreadCount++;
        this.scrollToLatest();
      });
    }, 300);
  }

  private pushBotMessage(partial: Partial<ChatMessage>): void {
    this.messages = [...this.messages, {
      sender: 'bot',
      text: '',
      timestamp: new Date(),
      ...partial
    }];
    if (!this.isOpen) this.unreadCount++;
    this.scrollToLatest();
  }

  private async getFrontendReply(message: string): Promise<ChatMessage> {
    const intent = this.detectIntent(message);

    if (this.isHrRole) {

      if (intent === 'hr_dashboard' || intent === 'greeting' || intent === 'help') {
        if (intent === 'hr_dashboard') {
          try {
            const res: any = await this.apiService.get('/dashboard/stats').toPromise();
            const s = res?.data || res || {};
            const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' });
            const cards: ChatCard[] = [
              { label: 'Present Today',     value: `${s.presentToday ?? '—'}`,     sub: 'Checked in',         highlight: true },
              { label: 'Pending Leaves',    value: `${s.pendingLeaves ?? '—'}`,    sub: 'Awaiting approval',  highlight: (s.pendingLeaves || 0) > 0 },
              { label: 'Active Employees',  value: `${s.activeEmployees ?? '—'}`,  sub: 'Total active' },
              { label: 'Departments',       value: `${s.totalDepartments ?? '—'}`, sub: 'In organisation' },
            ];
            return {
              sender: 'bot',
              type: 'cards',
              text: `Good ${this.timeOfDay()} — here's your org pulse for ${today}:`,
              cards,
              suggestedActions: ['Pending leave approvals', 'Who is absent today', 'Onboarding pipeline'],
            };
          } catch {
            return { sender: 'bot', text: "Sorry, I couldn't fetch the dashboard stats right now." };
          }
        }
      }

      if (intent === 'hr_pending_leaves') {
        try {
          const response: any = await this.apiService.get('/leaves').toPromise();
          const all = Array.isArray(response) ? response : response?.data || [];
          const pending = all.filter((l: any) => (l.status || '').toLowerCase() === 'pending');
          const cards: ChatCard[] = pending.slice(0, 6).map((l: any) => ({
            label: l.employeeName || l.employeeId || 'Employee',
            value: `${this.toTitleCase(l.leaveType || 'Leave')} · ${l.days || '—'} day(s)`,
            sub: `${this.formatDate(l.startDate)} → ${this.formatDate(l.endDate)}`,
            highlight: false
          }));
          return {
            sender: 'bot',
            type: cards.length ? 'cards' : 'text',
            text: pending.length
              ? `${pending.length} leave request${pending.length > 1 ? 's' : ''} pending approval:`
              : '✅ No pending leave requests right now.',
            cards,
            linkText: 'Open Leave Management',
            route: ['/leaves'],
            suggestedActions: ["Today's pulse", 'Who is absent today', 'Headcount']
          };
        } catch {
          return { sender: 'bot', text: "Sorry, I couldn't fetch pending leaves right now." };
        }
      }

      if (intent === 'hr_attendance_today') {
        try {
          const today = this.localDate(new Date());
          const [attRes, empRes]: any[] = await Promise.all([
            this.apiService.get('/attendance?date=' + today).toPromise(),
            this.apiService.get('/employees').toPromise(),
          ]);
          const records  = Array.isArray(attRes) ? attRes : attRes?.data || [];
          const employees = Array.isArray(empRes) ? empRes : empRes?.data || [];
          const presentIds = new Set(records.filter((r: any) => r.checkIn).map((r: any) => r.employeeId));
          const totalActive = employees.filter((e: any) => (e.status || '').toLowerCase() === 'active').length;
          const present = presentIds.size;
          const absent  = Math.max(0, totalActive - present);

          const absentList: ChatCard[] = employees
            .filter((e: any) => (e.status || '').toLowerCase() === 'active' && !presentIds.has(e.id))
            .slice(0, 6)
            .map((e: any) => ({
              label: `${e.firstName || ''} ${e.lastName || ''}`.trim() || e.email,
              value: e.department || e.designation || '—',
              sub: 'Not checked in'
            }));

          return {
            sender: 'bot',
            type: 'cards',
            text: `Attendance for today — ${present} present, ${absent} absent (${totalActive} active):`,
            cards: [
              { label: 'Present',        value: `${present}`,      sub: 'Checked in today', highlight: true },
              { label: 'Absent',         value: `${absent}`,       sub: 'Not checked in',   highlight: absent > 0 },
              { label: 'Total Active',   value: `${totalActive}`,  sub: 'Active employees' },
              ...absentList
            ],
            linkText: 'Open Attendance',
            route: ['/attendance'],
            suggestedActions: ['Pending leave approvals', "Today's pulse", 'Headcount']
          };
        } catch {
          return { sender: 'bot', text: "Sorry, I couldn't fetch today's attendance." };
        }
      }

      if (intent === 'hr_headcount') {
        try {
          const response: any = await this.apiService.get('/employees').toPromise();
          const employees = Array.isArray(response) ? response : response?.data || [];
          const active   = employees.filter((e: any) => (e.status || '').toLowerCase() === 'active').length;
          const inactive = employees.filter((e: any) => (e.status || '').toLowerCase() !== 'active').length;
          const now      = new Date();
          const thisMonth = employees.filter((e: any) => {
            const d = new Date(e.joiningDate || e.createdAt || '');
            return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
          }).length;
          return {
            sender: 'bot',
            type: 'cards',
            text: `Headcount overview (${employees.length} total employees):`,
            cards: [
              { label: 'Total Employees', value: `${employees.length}`,  sub: 'In system', highlight: true },
              { label: 'Active',          value: `${active}`,           sub: 'Currently active' },
              { label: 'Inactive',        value: `${inactive}`,         sub: 'On hold / exited' },
              { label: 'Joined This Month', value: `${thisMonth}`,      sub: now.toLocaleString('default', { month: 'long', year: 'numeric' }) },
            ],
            linkText: 'Open Employees',
            route: ['/employees'],
            suggestedActions: ['Onboarding pipeline', "Today's pulse", 'Pending leave approvals']
          };
        } catch {
          return { sender: 'bot', text: "Sorry, I couldn't fetch headcount data right now." };
        }
      }

      if (intent === 'hr_onboarding') {
        try {
          const response: any = await this.apiService.get('/candidates').toPromise();
          const candidates = Array.isArray(response) ? response : response?.data || [];
          const byStatus: Record<string, number> = {};
          candidates.forEach((c: any) => {
            const s = (c.status || 'unknown').toLowerCase();
            byStatus[s] = (byStatus[s] || 0) + 1;
          });
          const cards: ChatCard[] = Object.entries(byStatus).map(([status, count]) => ({
            label: this.toTitleCase(status),
            value: `${count} candidate${count > 1 ? 's' : ''}`,
            highlight: status === 'invited' || status === 'in_review'
          }));
          return {
            sender: 'bot',
            type: cards.length ? 'cards' : 'text',
            text: candidates.length
              ? `Onboarding pipeline — ${candidates.length} candidate(s) total:`
              : 'No candidates in the onboarding pipeline right now.',
            cards,
            linkText: 'Open Onboarding',
            route: ['/onboarding'],
            suggestedActions: ['Headcount', "Today's pulse", 'Pending leave approvals']
          };
        } catch {
          return { sender: 'bot', text: "Sorry, I couldn't fetch the onboarding pipeline." };
        }
      }

      if (intent === 'hr_payroll_status') {
        try {
          const now   = new Date();
          const month = now.getMonth() + 1;
          const year  = now.getFullYear();
          const response: any = await this.apiService.get(`/payroll/month/${month}/year/${year}`).toPromise();
          const payrolls = Array.isArray(response) ? response : response?.data || [];
          const generated = payrolls.filter((p: any) => p.status === 'generated' || p.status === 'paid').length;
          const pending   = payrolls.filter((p: any) => !p.status || p.status === 'pending').length;
          const monthLabel = now.toLocaleString('default', { month: 'long', year: 'numeric' });
          return {
            sender: 'bot',
            type: 'cards',
            text: `Payroll status for ${monthLabel}:`,
            cards: [
              { label: 'Total Records',  value: `${payrolls.length}`, sub: 'Payroll entries' },
              { label: 'Generated/Paid', value: `${generated}`,       sub: 'Processed',  highlight: generated > 0 },
              { label: 'Pending',        value: `${pending}`,         sub: 'Not processed', highlight: pending > 0 },
            ],
            linkText: 'Open Payroll',
            route: ['/payroll'],
            suggestedActions: ['Headcount', "Today's pulse", 'Pending leave approvals']
          };
        } catch {
          return { sender: 'bot', text: "Sorry, I couldn't fetch payroll status right now." };
        }
      }

      const hrRes = HR_INTENT_RESPONSES[intent] || HR_INTENT_RESPONSES['default'];
      return {
        sender: 'bot',
        text: hrRes.response,
        linkText: hrRes.linkText,
        route: hrRes.route,
        queryParams: hrRes.queryParams,
        suggestedActions: hrRes.suggestedActions
      };
    }


    if (intent === 'leave_balance') {
      try {
        const employeeId = this.getCurrentEmployeeId();
        const response: any = await this.apiService.get('/leave-balances?employeeId=' + employeeId).toPromise();
        const cards = this.buildLeaveBalanceCards(response);
        return {
          sender: 'bot',
          type: cards.length ? 'cards' : 'text',
          text: cards.length ? 'Here is your current leave balance:' : 'No leave balance data available right now.',
          cards,
          suggestedActions: INTENT_RESPONSES['leave_balance'].suggestedActions
        };
      } catch {
        return { sender: 'bot', text: 'Sorry, I could not fetch your leave balance right now. Please try again.' };
      }
    }

    if (intent === 'leave_status') {
      try {
        const response: any = await this.apiService.get('/leaves').toPromise();
        const leaves = Array.isArray(response) ? response : response?.data || [];
        return {
          sender: 'bot',
          type: leaves.length ? 'cards' : 'text',
          text: leaves.length ? 'Here are your recent leave applications:' : 'No leave applications found.',
          cards: this.buildLeaveStatusCards(leaves),
          linkText: 'Open Leave History',
          route: ['/leaves'],
          suggestedActions: ['Apply for leave', 'Leave balance', 'Leave policy']
        };
      } catch {
        return { sender: 'bot', text: 'Sorry, I could not fetch your leave applications right now.' };
      }
    }

    if (intent === 'attendance_today') {
      try {
        const employeeId = this.getCurrentEmployeeId();
        const today = this.localDate(new Date());
        const response: any = await this.apiService.get('/attendance?date=' + today).toPromise();
        const records = Array.isArray(response) ? response : response?.data || [];
        const myRecords = records.filter((x: any) => x.employeeId === employeeId);
        const cards = this.buildTodayAttendanceCards(myRecords);
        return {
          sender: 'bot',
          type: cards.length ? 'cards' : 'text',
          text: cards.length ? "Here's your attendance for today:" : 'No attendance record found for today.',
          cards,
          suggestedActions: INTENT_RESPONSES['attendance_today'].suggestedActions,
          linkText: 'Open Attendance',
          route: ['/attendance']
        };
      } catch {
        return { sender: 'bot', text: "Sorry, I couldn't fetch today's attendance." };
      }
    }

    if (intent === 'attendance_monthly') {
      try {
        const response: any = await this.apiService.get('/attendance/my').toPromise();
        const allRecords = Array.isArray(response) ? response : response?.data || [];
        const cards = this.buildMonthlyAttendanceCards(allRecords);
        return {
          sender: 'bot',
          type: 'cards',
          text: `Attendance summary for ${new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}:`,
          cards,
          suggestedActions: INTENT_RESPONSES['attendance_monthly'].suggestedActions,
          linkText: 'Open Attendance',
          route: ['/attendance']
        };
      } catch {
        return { sender: 'bot', text: 'Sorry, monthly attendance is not available right now.' };
      }
    }

    if (intent === 'holiday_list' || intent === 'holiday_upcoming') {
      try {
        const response: any = await this.apiService.get('/holidays').toPromise();
        const holidays = Array.isArray(response) ? response : [];
        const upcoming = this.getUpcomingHolidays(holidays, intent === 'holiday_upcoming' ? 5 : 10);
        const cards = upcoming.map(h => ({
          label: h.name || 'Holiday',
          value: this.formatHolidayDate(h.date),
          sub: h.type || '',
        }));
        return {
          sender: 'bot',
          type: cards.length ? 'cards' : 'text',
          text: cards.length
            ? (intent === 'holiday_upcoming' ? 'Upcoming holidays:' : 'Holiday calendar:')
            : 'No holidays found in the system.',
          cards,
          suggestedActions: ['Apply for leave', 'Leave balance'],
          linkText: 'Open Holidays',
          route: ['/holidays']
        };
      } catch {
        return { sender: 'bot', text: 'Sorry, I could not fetch the holiday list right now.' };
      }
    }

    if (intent === 'payroll_payslip') {
      try {
        const response: any = await this.apiService.get('/payroll/my-payslips').toPromise();
        const payslips = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        const cards = this.buildPayslipCards(payslips);
        return {
          sender: 'bot',
          type: cards.length ? 'cards' : 'text',
          text: cards.length ? 'Here are your recent payslips:' : 'No payslips found yet.',
          cards,
          suggestedActions: ['Salary details', 'Payroll date', 'Leave balance'],
          linkText: 'Open My Payslips',
          route: ['/my-payslips']
        };
      } catch {
        return { sender: 'bot', text: 'Sorry, I could not fetch your payslips right now.' };
      }
    }

    if (intent === 'company_announcements') {
      try {
        const response: any = await this.apiService.get('/announcements').toPromise();
        const announcements = Array.isArray(response) ? response : [];
        const recent = announcements.slice(0, 4);
        const cards = recent.map((a: any) => ({
          label: a.title || 'Announcement',
          value: a.content ? (a.content.length > 80 ? a.content.substring(0, 80) + '…' : a.content) : '',
          sub: a.createdAt ? new Date(a.createdAt).toLocaleDateString() : '',
        }));
        return {
          sender: 'bot',
          type: cards.length ? 'cards' : 'text',
          text: cards.length ? 'Latest company announcements:' : 'No announcements at the moment.',
          cards,
          suggestedActions: ['Company policy', 'Dashboard', 'Contact HR'],
          linkText: 'Open Announcements',
          route: ['/announcements']
        };
      } catch {
        return { sender: 'bot', text: 'Sorry, I could not fetch announcements right now.' };
      }
    }

    const res = INTENT_RESPONSES[intent] || INTENT_RESPONSES['default'];
    return {
      sender: 'bot',
      text: res.response,
      linkText: res.linkText,
      route: res.route,
      queryParams: res.queryParams,
      suggestedActions: res.suggestedActions
    };
  }


  private buildLeaveBalanceCards(response: unknown): ChatCard[] {
    const balances = Array.isArray(response)
      ? response
      : (response as any)?.data
      ? (response as any).data
      : [];
    if (!balances.length) return [];

    const grouped = new Map<string, { leaveType: string; total: number; used: number }>();
    balances.forEach((b: any) => {
      const key = String(b.leaveType || 'leave').toLowerCase();
      const cur = grouped.get(key) || { leaveType: key, total: 0, used: 0 };
      cur.total += Number(b.totalDays || 0);
      cur.used += Number(b.usedDays || 0);
      grouped.set(key, cur);
    });

    return Array.from(grouped.values()).map(b => {
      const remaining = Math.max(0, b.total - b.used);
      return {
        label: this.toTitleCase(b.leaveType),
        value: `${remaining} days left`,
        sub: `${b.used} used · ${b.total} total`,
        highlight: remaining === 0
      };
    });
  }

  private buildLeaveStatusCards(leaves: any[]): ChatCard[] {
    return leaves.slice(0, 5).map(l => ({
      label: `${this.toTitleCase(l.leaveType || 'Leave')} — ${l.status || 'Pending'}`,
      value: `${this.formatDate(l.startDate)} → ${this.formatDate(l.endDate)}`,
      sub: l.reason ? (l.reason.length > 50 ? l.reason.substring(0, 50) + '…' : l.reason) : '',
      highlight: (l.status || '').toLowerCase() === 'rejected'
    }));
  }

  private buildTodayAttendanceCards(records: any[]): ChatCard[] {
    if (!records?.length) return [];
    const checkedIn = records.filter(r => r.checkIn);
    if (!checkedIn.length) return [];

    checkedIn.sort((a, b) => String(a.checkIn).localeCompare(String(b.checkIn)));
    const firstIn = checkedIn[0].checkIn;
    const completed = checkedIn.filter(r => r.checkOut);
    const lastOut = completed.length ? completed[completed.length - 1].checkOut : null;

    let totalMinutes = 0;
    checkedIn.forEach(rec => {
      const inMs = this.timeToMs(rec.checkIn);
      const outMs = rec.checkOut ? this.timeToMs(rec.checkOut) : Date.now() - new Date().setHours(0,0,0,0);
      if (inMs !== null && outMs !== null) totalMinutes += (outMs - inMs) / 60000;
    });
    const h = Math.floor(totalMinutes / 60);
    const m = Math.floor(totalMinutes % 60);

    return [
      { label: 'Check In', value: this.formatTime(firstIn), highlight: false },
      { label: 'Check Out', value: lastOut ? this.formatTime(lastOut) : '—', sub: lastOut ? '' : 'Not yet' },
      { label: 'Total Hours', value: `${h}h ${m}m`, highlight: h >= 8 },
      { label: 'Sessions', value: `${checkedIn.length}` },
    ];
  }

  private buildMonthlyAttendanceCards(allRecords: any[]): ChatCard[] {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const today = now.getDate();

    const monthRecords = allRecords.filter(r => {
      const d = new Date(r.date || r.attendanceDate || '');
      return d.getFullYear() === year && d.getMonth() === month;
    });

    const uniqueDays = new Map<string, any[]>();
    monthRecords.forEach(r => {
      const date = String(r.date || r.attendanceDate || '').split('T')[0];
      if (!uniqueDays.has(date)) uniqueDays.set(date, []);
      uniqueDays.get(date)!.push(r);
    });

    let present = 0, late = 0, workingDays = 0;
    for (let d = 1; d <= today; d++) {
      const day = new Date(year, month, d).getDay();
      if (day === 0 || day === 6) continue;
      workingDays++;
      const date = `${year}-${String(month + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      const dayRecs = uniqueDays.get(date) || [];
      if (dayRecs.some(r => r.checkIn)) {
        present++;
        if (dayRecs.some(r => String(r.status || '').toLowerCase() === 'late')) late++;
      }
    }
    const absent = workingDays - present;
    const pct = workingDays ? Math.round((present / workingDays) * 100) : 0;

    return [
      { label: 'Present', value: `${present} days`, highlight: true },
      { label: 'Absent', value: `${absent} days`, highlight: absent > 3 },
      { label: 'Late', value: `${late} days` },
      { label: 'Working Days', value: `${workingDays} days` },
      { label: 'Attendance %', value: `${pct}%`, highlight: pct >= 90 },
    ];
  }

  private buildPayslipCards(payslips: any[]): ChatCard[] {
    return payslips.slice(0, 4).map(p => ({
      label: p.month && p.year ? `${p.month} ${p.year}` : this.formatDate(p.createdAt || p.date || ''),
      value: p.netPay != null ? `₹${Number(p.netPay).toLocaleString('en-IN')}` : 'View details',
      sub: p.status ? this.toTitleCase(p.status) : ''
    }));
  }

  private getUpcomingHolidays(holidays: any[], limit: number): any[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return holidays
      .filter(h => h.date && new Date(h.date) >= today)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, limit);
  }


  private timeOfDay(): string {
    const h = new Date().getHours();
    if (h < 12) return 'morning';
    if (h < 17) return 'afternoon';
    return 'evening';
  }

  private detectIntent(message: string): string {
    const normalized = message.toLowerCase().trim().replace(/[^\w\s]/g, '');

    if (this.isHrRole) {
      const hrKeywordMap: Record<string, string[]> = INTENT_KEYWORDS;
      const hrIntents = ['hr_dashboard', 'hr_pending_leaves', 'hr_attendance_today', 'hr_headcount', 'hr_onboarding', 'hr_payroll_status'];
      for (const intent of hrIntents) {
        const kws = hrKeywordMap[intent] || [];
        for (const kw of kws) {
          if (normalized.includes(kw.toLowerCase())) return intent;
        }
      }
    }

    for (const [intent, keywords] of Object.entries(INTENT_KEYWORDS)) {
      for (const keyword of keywords) {
        if (normalized.includes(keyword.toLowerCase())) return intent;
      }
    }

    if (normalized.includes('leave') && (normalized.includes('balance') || normalized.includes('remaining') || normalized.includes('available')))
      return 'leave_balance';
    if (normalized.includes('attendance') && normalized.includes('today'))
      return 'attendance_today';
    if (normalized.includes('attendance') && (normalized.includes('month') || normalized.includes('monthly')))
      return 'attendance_monthly';
    if (normalized.includes('payslip') || normalized.includes('salary slip'))
      return 'payroll_payslip';

    return 'default';
  }


  private formatHolidayDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', weekday: 'short' });
  }

  private formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  private formatTime(time: string): string {
    if (!time) return '—';
    const parts = String(time).split(':');
    if (parts.length < 2) return time;
    const h = parseInt(parts[0], 10);
    const m = parts[1].padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m} ${ampm}`;
  }

  private timeToMs(time: string): number | null {
    if (!time) return null;
    const parts = String(time).split(':');
    if (parts.length < 2) return null;
    return (parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2] || '0')) * 1000;
  }

  private toTitleCase(value: string): string {
    return value.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  private getCurrentEmployeeId(): string {
    const user = this.authService.getCurrentUser();
    return user?.id || user?.employeeId || '';
  }

  private localDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private scrollToLatest(): void {
    setTimeout(() => {
      const el = this.messagesContainer?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 50);
  }
}
